import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from '../components/Icon';
import { PageHero, SectionHead, ArrowButton, StatsRow, CTABand } from '../components/ui';
import ScrollText from '../motion/ScrollText';
import VelocityMarquee from '../motion/VelocityMarquee';
import { Reveal, Stagger, StaggerItem, EASE } from '../motion/Reveal';
import { PHASES, ROLES, VALUES } from '../data/site';

const LEAD_STATS = [
  { end: 10, suffix: '+', label: 'Years of Experience', sub: 'Civil construction PMC since 2014' },
  { end: 6, suffix: '+', label: 'Specialist Roles', sub: 'Multi-tier leadership framework' },
  { end: 360, suffix: '°', label: 'Site Oversight', sub: 'Pre · Build · Post-construction' },
  { end: 100, suffix: '%', label: 'Client Commitment', sub: 'Quality, safety & on-time' },
];
const LEAD_TICKER = ['Strategic Governance', 'QA / QC Excellence', 'Risk Mitigation', 'Cost Optimisation', 'Transparent Reporting', 'Timely Delivery', 'DPR Monitoring', 'Seamless Handover'];

function FlipCard({ r, i }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <StaggerItem>
      <div className="flip" onClick={() => setFlipped((f) => !f)} onMouseEnter={() => window.matchMedia('(hover: hover)').matches && setFlipped(true)}
        onMouseLeave={() => setFlipped(false)} role="button" tabIndex={0} aria-pressed={flipped}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setFlipped((f) => !f))}>
        <motion.div className="flip-inner" animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}>
          <div className="flip-face flip-front">
            <span className="flip-abbr">{r.abbr}</span>
            <h3 className="h3">{r.role}</h3>
            <span className="flip-phase">{r.phase}</span>
            <p>{r.desc}</p>
            <span className="flip-hint"><Icon name="refresh" size={14} />Hover or tap to flip</span>
          </div>
          <div className="flip-face flip-back">
            <span className="card-num" style={{ color: '#60A5FA' }}>0{i + 1} · Responsibilities</span>
            <h3 className="h3" style={{ marginTop: 10 }}>{r.role}</h3>
            <ul className="check-list">{r.responsibilities.map((x) => <li key={x}><Icon name="checkCircle" size={18} />{x}</li>)}</ul>
          </div>
        </motion.div>
      </div>
    </StaggerItem>
  );
}

export default function Leadership() {
  const [phase, setPhase] = useState(0);
  const p = PHASES[phase];
  return (
    <>
      <PageHero
        title="The people behind|every *milestone*"
        lead="A multi-tier leadership framework that ensures accountability from PMC to site — every phase owned, every milestone tracked, every decision accountable."
        images={['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=900&h=720&fit=crop&q=68&auto=format', 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=560&h=560&fit=crop&q=68&auto=format']}>
        <ArrowButton to="/request-consultation">Talk to our team</ArrowButton>
        <ArrowButton href="#lifecycle" variant="outline">See the lifecycle</ArrowButton>
      </PageHero>

      <VelocityMarquee items={LEAD_TICKER} className="dark" />

      {/* Lifecycle tabs */}
      <section className="section" id="lifecycle" style={{ scrollMarginTop: 60 }}>
        <div className="container">
          <SectionHead center num="01" kicker="Project lifecycle" title="Multi-tier governance,|*every* phase" lead="Dedicated, phase-specific leadership — the right expertise governs every stage from planning through handover." />
          <Reveal style={{ textAlign: 'center' }}>
            <div className="seg" role="tablist">
              {PHASES.map((ph, i) => (
                <button key={ph.label} role="tab" aria-selected={phase === i} className={phase === i ? 'on' : ''} onClick={() => setPhase(i)}>
                  <motion.span className="seg-pill" initial={false} animate={{ opacity: phase === i ? 1 : 0, scale: phase === i ? 1 : 0.85 }} transition={{ type: 'spring', stiffness: 380, damping: 30 }} />
                  <Icon name={ph.icon} size={18} />{ph.label}
                </button>
              ))}
            </div>
          </Reveal>
          <AnimatePresence mode="wait">
            <motion.div key={phase} className="phase-panel" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.55, ease: EASE }}>
              <motion.div className="phase-media" initial={{ clipPath: 'inset(0 100% 0 0 round 30px)' }} animate={{ clipPath: 'inset(0 0% 0 0 round 30px)' }} transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}>
                <motion.img src={p.image} alt={p.title} initial={{ scale: 1.3 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: EASE }} />
              </motion.div>
              <div>
                <span className="card-num">Phase {phase + 1} · {p.label}</span>
                <h3 className="h2" style={{ fontSize: 'clamp(1.8rem,3.2vw,2.6rem)', margin: '12px 0 16px' }}>{p.title}</h3>
                <p className="lead">{p.desc}</p>
                <div className="phase-stat"><strong>{p.stat.val}</strong><span>{p.stat.unit} · {p.stat.label}</span></div>
                <h4 className="kicker" style={{ marginBottom: 14 }}>Roles in this phase</h4>
                <Stagger style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }} gap={0.06}>
                  {p.roles.map((r) => <StaggerItem key={r} as="span" className="role-pill">{r}</StaggerItem>)}
                </Stagger>
                <div className="tag-row">{p.keywords.map((k) => <span className="tag" key={k}>{k}</span>)}</div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Roles — 3D flip cards */}
      <section className="section section-alt">
        <div className="container">
          <SectionHead center num="02" kicker="Specialist roles" title="The right expert in|*every* seat" lead="Hover (or tap) a card to flip it and see each role's responsibilities — no overlap, no gaps." />
          <Stagger className="flip-grid" gap={0.08}>
            {ROLES.map((r, i) => <FlipCard key={r.role} r={r} i={i} />)}
          </Stagger>
        </div>
      </section>

      <section className="section-sm"><div className="container"><StatsRow items={LEAD_STATS} /></div></section>

      {/* Values — sticky title, scrolling list (dark) */}
      <section className="section section-dark">
        <div className="aurora" />
        <div className="container sticky-split" style={{ position: 'relative' }}>
          <div className="sticky-side">
            <SectionHead num="03" kicker="Company values" title="Quality, safety &|*client satisfaction*" lead="Not aspirations — the standards by which every Blue Block engagement is measured." />
          </div>
          <div>
            {VALUES.map((v) => (
              <Reveal key={v.title} from="up" className="value-row">
                <div className="icon-box"><Icon name={v.icon} size={24} /></div>
                <div><h3 className="h3">{v.title}</h3><p>{v.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Executive summary */}
      <section className="section">
        <div className="container">
          <div className="statement">
            <Reveal from="none" className="kicker"><b>04</b>Executive summary</Reveal>
            <div>
              <ScrollText text="Guiding your construction journey with innovation — crafting intelligent buildings with excellence. Precision at every phase, accountability at every level." />
              <Reveal delay={0.1} style={{ marginTop: 40 }}><ArrowButton to="/request-consultation">Start the conversation</ArrowButton></Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
